import { AiDashboard } from '@app/components/icons/ai-dashboard';
import { AiEmail } from '@app/components/icons/ai-email';
import { AIStarIcon } from '@app/components/icons/ai-star';
import { AiWriter } from '@app/components/icons/ai-writer';
import { Crown } from '@app/components/nav-bar/crown';
import { FLAG_CONTENT_HUB_ENABLE_UPGRADE_BANNER } from '@app/feature-flags/flags';
import { useFlag } from '@app/feature-flags/use-flag';
import { selectContentHubConfig } from '@app/routes/content/selectors';
import { selectIsAiDisabled } from '@app/routes/organization/selectors';
import { useAppSelector } from '@app/store/store';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { createElement, useMemo } from 'react';

import { useUnseenContent } from '../components/content-composer/hooks';
import { selectContentComposerContentHistory } from '../components/content-composer/selector';
import {
  ContentGeneration,
  Landing,
  UpgradeBanner,
} from '../components/content-composer/views';
import { Appearance as AppearancePanel } from '../components/contex-menu/appearance';
import { Details as DetailsPanel } from '../components/contex-menu/details';
import { Transcript as TranscriptPanel } from '../components/contex-menu/transcript';
import type { ContextMenuItem } from '../components/context-menu-v2/context-menu-tab';
import { EditTranscript as EditTranscriptIcon } from '../components/icons';
import { Info } from '../components/icons/info';
import { Palette } from '../components/icons/palette';
import { EditAppearancePanel } from '../routes/editor/components/edit-view/components/edit-appearance-panel';
import { EditTranscript as EditTranscriptPanel } from '../routes/editor/components/edit-view/components/edit-transcript';
import {
  selectAspectRatio,
  selectDraftClips,
  selectRecording,
  selectSelectedClip,
} from '../selector';

/** `'default'` — full Creator Lab panel; `'edit'` — advanced edit route (transcript + appearance only). */
export type RightPanelGroupsPreset = 'default' | 'edit';

export type UseRightPanelGroupsOptions = {
  preset?: RightPanelGroupsPreset;
};

export const useRightPanelGroups = (
  options: UseRightPanelGroupsOptions = {},
) => {
  const { preset = 'default' } = options;
  const recording = useAppSelector(selectRecording);
  const draftClips = useAppSelector(selectDraftClips);
  const contentHistory = useAppSelector(selectContentComposerContentHistory);
  const hasUnseenSocialPost = useUnseenContent('SOCIAL_POST');
  const hasUnseenBlogPost = useUnseenContent('BLOG_POST');
  const hasUnseenEmail = useUnseenContent('EMAIL');

  const aspectRatio = useAppSelector(selectAspectRatio);
  const selectedClip = useAppSelector(selectSelectedClip);
  const isClip = recording?.is_original === false;
  const isBackupOrRehearsal =
    recording?.category === 'backup' || recording?.category === 'rehearsal';

  const isUpgradeBannerEnabled = useFlag(
    FLAG_CONTENT_HUB_ENABLE_UPGRADE_BANNER,
  );
  const contentHubConfig = useAppSelector(selectContentHubConfig);
  const isContentHubConfigLoaded = contentHubConfig.status === 'success';
  const hasContentHubCreatorLabAccess =
    contentHubConfig.hasContentHubCreatorLabAccess ?? false;
  const isAiDisabled = useAppSelector(selectIsAiDisabled);

  // Determine if we should show upgrade banner instead of Content Composer
  const showContentComposerUpgradeBanner =
    isUpgradeBannerEnabled &&
    isContentHubConfigLoaded &&
    !hasContentHubCreatorLabAccess;

  const { t } = useTranslation();

  const hasContentHistoryForType = useMemo(() => {
    return {
      SOCIAL_POST: Boolean(contentHistory['SOCIAL_POST']?.history?.length),
      BLOG_POST: Boolean(contentHistory['BLOG_POST']?.history?.length),
      EMAIL: Boolean(contentHistory['EMAIL']?.history?.length),
    };
  }, [contentHistory]);

  const groups = useMemo(() => {
    const showSelectClipOverlay =
      aspectRatio !== '9_16' && draftClips.length > 0 && selectedClip == null;

    // Shared appearance tab used by both presets, eliminating duplication.
    // The select-clip overlay is accessible: role="status" announces the message
    // to screen readers when it appears, and aria-live="polite" avoids interrupting
    // ongoing speech.
    const appearanceTab: ContextMenuItem = {
      id: 'video-appearance',
      icon: createElement(Palette, { className: 'size-5' }),
      panel: createElement(
        'div',
        { key: 'appearance', className: 'relative h-full' },
        createElement(AppearancePanel, {
          disabled: showSelectClipOverlay,
          scrollLocked: showSelectClipOverlay,
        }),
        showSelectClipOverlay &&
          createElement(
            'div',
            {
              role: 'status',
              'aria-live': 'polite',
              className:
                'absolute inset-x-0 -top-4 -bottom-4 z-10 flex items-center justify-center bg-spring-neutral-b6/85 backdrop-blur-sm',
            },
            createElement(
              'p',
              {
                className: 'text-center px-6 text-white text-sm font-medium',
              },
              t('content-recording.appearance.select-clip-message'),
            ),
          ),
      ),
      widthClassName: 'w-[36rem]',
      hidden: isClip || isBackupOrRehearsal,
    };

    const editAppearanceTab: ContextMenuItem = {
      id: 'video-appearance',
      icon: createElement(Palette, { className: 'size-5' }),
      panel: createElement(EditAppearancePanel, { key: 'edit-appearance' }),
      widthClassName: 'w-[36rem]',
      hidden: isClip || isBackupOrRehearsal,
    };

    /** Advanced edit view: transcript + appearance only (no details, no AI/content group). */
    if (preset === 'edit') {
      return [
        {
          id: 'video',
          tabs: [
            {
              id: 'video-transcript',
              icon: createElement(EditTranscriptIcon, { className: 'size-5' }),
              panel: createElement(EditTranscriptPanel, {
                key: 'edit-transcript',
              }),
              widthClassName: 'w-[36rem]',
            },
            editAppearanceTab,
          ] as ContextMenuItem[],
        },
      ];
    }

    // NOTE: The hooks below (useUnseenContent, content history, upgrade banner flag,
    // isAiDisabled) are only consumed by the 'default' preset. They are intentionally
    // called unconditionally to satisfy Rules of Hooks. If this hook grows significantly,
    // consider splitting into preset-specific hooks.

    // Content Composer tab - show Crown icon and UpgradeBanner if user doesn't have license
    const contentComposerTab: ContextMenuItem = {
      id: 'content-composer',
      widthClassName: 'w-[36rem]',
      icon: showContentComposerUpgradeBanner
        ? createElement(Crown, { className: 'size-5' })
        : createElement(AIStarIcon, { className: 'size-5' }),
      panel: showContentComposerUpgradeBanner
        ? createElement(UpgradeBanner, {
            key: 'content-composer-upgrade',
          })
        : createElement(Landing, { key: 'content-composer' }),
    };

    const contentTabs: ContextMenuItem[] = [contentComposerTab];

    // Only add content type tabs if user has license and has history
    if (!showContentComposerUpgradeBanner) {
      if (hasContentHistoryForType.SOCIAL_POST) {
        contentTabs.push({
          id: 'content-social-post',
          icon: createElement(AiDashboard, { className: 'size-5' }),
          panel: createElement(ContentGeneration, {
            contentType: 'SOCIAL_POST',
          }),
          widthClassName: 'w-[36rem]',
          showNotificationBadge: hasUnseenSocialPost,
        });
      }
      if (hasContentHistoryForType.BLOG_POST) {
        contentTabs.push({
          id: 'content-blog-post',
          icon: createElement(AiWriter, { className: 'size-5' }),
          panel: createElement(ContentGeneration, {
            contentType: 'BLOG_POST',
          }),
          widthClassName: 'w-[36rem]',
          showNotificationBadge: hasUnseenBlogPost,
        });
      }
      if (hasContentHistoryForType.EMAIL) {
        contentTabs.push({
          id: 'content-email',
          icon: createElement(AiEmail, { className: 'size-5' }),
          panel: createElement(ContentGeneration, {
            contentType: 'EMAIL',
          }),
          widthClassName: 'w-[36rem]',
          showNotificationBadge: hasUnseenEmail,
        });
      }
    }

    return [
      {
        id: 'video',
        tabs: [
          {
            id: 'video-details',
            icon: createElement(Info, { className: 'size-5' }),
            panel: createElement(DetailsPanel, { key: 'details' }),
            widthClassName: 'w-[36rem]',
          },
          {
            id: 'video-transcript',
            icon: createElement(EditTranscriptIcon, { className: 'size-5' }),
            panel: createElement(TranscriptPanel, { key: 'transcript' }),
            widthClassName: 'w-[36rem]',
          },
          appearanceTab,
        ] as ContextMenuItem[],
      },
      // Only include content group if AI is not disabled
      ...(isAiDisabled
        ? []
        : [
            {
              id: 'content',
              tabs: contentTabs,
            },
          ]),
    ];
  }, [
    preset,
    isAiDisabled,
    showContentComposerUpgradeBanner,
    hasContentHistoryForType.SOCIAL_POST,
    hasContentHistoryForType.BLOG_POST,
    hasContentHistoryForType.EMAIL,
    hasUnseenSocialPost,
    hasUnseenBlogPost,
    hasUnseenEmail,
    isClip,
    isBackupOrRehearsal,
    aspectRatio,
    draftClips.length,
    selectedClip,
    t,
  ]);

  return groups;
};
